import React from "react";
import Plot from "react-plotly.js";

import Card from "components/atoms/Card";

interface UsageDetailItem {
  keyName: string;
  keyValue: string;
  usage: number;
  count: number;
}

interface UsageDetailCardProps {
  usageDetail: Record<string, UsageDetailItem>;
  usageDetailGroupMode: string[];
  setUsageDetailGroupMode: (mode: string[]) => void;
  groupModes: string[][];
}

const UsageDetailCard: React.FC<UsageDetailCardProps> = ({
  usageDetail,
  usageDetailGroupMode,
  setUsageDetailGroupMode,
  groupModes,
}) => {
  const handleModeChange = (mode: string[]) => {
    setUsageDetailGroupMode(mode);
  };

  function trimPath(path: string, sublength: number, length: number): string {
    let parts: string[] = path.split("/");
    parts = parts.map((part) => {
      if (part.length > sublength) {
        return part.substring(0, sublength) + "...";
      }
      return part;
    });
    let trimmedPath: string = parts.join("/");
    if (trimmedPath.length > length) {
      trimmedPath = trimmedPath.substring(0, length) + "...";
    }
    return trimmedPath;
  }

  const formatTypeLabel = (value: string): string => {
    const normalized = (value || "").trim().toLowerCase();
    switch (normalized) {
      case "api":
        return "API Calls";
      case "textai":
        return "Text AI";
      case "visionai":
        return "Vision AI";
      case "chatai":
        return "Advisor / Chat";
      case "aisearch":
      case "semanticsearch":
        return "AI Search";
      default:
        return value;
    }
  };

  const formatGroupValue = (item: UsageDetailItem): string => {
    if (usageDetailGroupMode[1] === "type") {
      return formatTypeLabel(item.keyValue);
    }
    return item.keyValue;
  };

  const usageDetailItems = Object.values(
    usageDetail || {}
  ) as UsageDetailItem[];
  const totalTokensUsed = Object.values(usageDetail || {}).reduce(
    (sum, item) => sum + item.usage,
    0
  );

  return (
    <Card className="mt-2 w-full" borderRequired>
      <div className="flex flex-col">
        <div className="mb-3">
          <span className="text-lg font-semibold text-gray-900">
            Period Usage Detail
          </span>
        </div>

        <div className="flex justify-center space-x-2 mb-4 pt-2">
          {groupModes.map((mode, index) => (
            <button
              key={index}
              onClick={() => handleModeChange(mode)}
              className={`px-3 py-1 text-sm font-medium rounded-full ${
                usageDetailGroupMode[1] === mode[1]
                  ? "bg-indigo-600 text-white"
                  : "bg-indigo-100 text-indigo-700 hover:bg-gray-300"
              }`}
            >
              {mode[0]}
            </button>
          ))}
        </div>

        <div className="max-h-[640px] w-full overflow-y-auto grid grid-cols-12 auto-rows-auto gap-3">
          <div className="p-0 border border-gray-200 rounded-lg col-span-10">
            <table className="min-w-full rounded-lg col-span-10">
              <thead className="bg-gray-100 rounded-lg">
                <tr>
                  <th
                    scope="col"
                    className="py-3 px-3 text-left text-sm font-semibold text-gray-600 tracking-wide uppercase"
                  >
                    {usageDetailGroupMode[0]}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-sm font-semibold text-gray-600 tracking-wide uppercase"
                  >
                    Count
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-sm font-semibold text-gray-600 tracking-wide uppercase"
                  >
                    Tokens Used
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {usageDetailItems.map(
                  (item: UsageDetailItem, index: number) => (
                    <tr key={index}>
                      <td className="whitespace-nowrap py-2 px-3 text-sm font-medium text-gray-900 uppercase">
                        {trimPath(formatGroupValue(item), 16, 120)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-600 font-medium uppercase">
                        {item.count.toLocaleString("en-US")}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-600 font-medium uppercase">
                        {item.usage.toLocaleString("en-US")}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          <div className="col-span-2">
            <div className="w-full h-full p-6 bg-white rounded-lg border border-gray-200 flex-col justify-center items-center gap-1 inline-flex">
              <div className="self-stretch text-center text-gray-600 text-sm font-medium leading-tight">
                Total Tokens Used
              </div>
              <div className="self-stretch text-center text-indigo-600 text-3xl font-semibold leading-9">
                {totalTokensUsed.toLocaleString()}
              </div>
            </div>
          </div>

          <div className="w-full col-span-8 col-start-3 mt-2">
            <Plot
              data={[
                {
                  type: "pie" as const,
                  labels: usageDetailItems.map((detail) =>
                    formatGroupValue(detail)
                  ),
                  values: usageDetailItems.map((detail) => detail.usage),
                  text: usageDetailItems.map(
                    (detail) => `${detail.count} calls`
                  ),
                  textinfo: "value" as const,
                },
              ]}
              layout={{
                title: "Usage by Group",
                font: { size: 10 },
                autosize: true,
                height: 300,
                margin: { l: 30, r: 30, t: 40, b: 20 },
              }}
              useResizeHandler={true}
              style={{ width: "100%", height: "300px" }}
              config={{
                displayModeBar: false,
                responsive: true,
              }}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default UsageDetailCard;
